import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../api.js";
import { dayKey, formatDate, formatDay, formatFullDay, formatTime, isoDay } from "../format.js";
import { MoveTimeRequestEntry } from "../waitlist/RequestTimePicker.js";
import {
  booking,
  idempotencyKey,
  type AvailabilitySlot,
  type CancellationPreview,
  type MutationResult,
  type ReschedulePreview,
  type StudentLesson,
} from "./api.js";
import {
  availabilityWindow,
  groupAvailabilitySlots,
  moveAvailabilityWindow,
  type AvailabilityRange,
} from "./availabilityRange.js";
import { bookingFailureKind } from "./attempt.js";
import {
  describeCredit,
  describePayment,
  describePaymentEffect,
  lateCancellationMessage,
  moveAllowed,
  Reasons,
  selectableForMove,
} from "./policy.js";
import "./familyLessons.css";

function cancellationOutcome(result: MutationResult, lesson: StudentLesson): ActionOutcome {
  return { variant: result.credit_sync === "pending_reconciliation" ? "attention" : "success", title: "Lesson cancelled",
    description: `${formatDate(lesson.startsAt)} · ${formatTime(lesson.startsAt)}`,
    details: [
      { value: describeCredit(result.credit, "confirmed") },
      ...(result.credit_sync === "pending_reconciliation" ? [{ value: "The lesson is cancelled, but the lesson credits on the account aren't final yet. The office has been told and will put this right." }] : []),
      { value: result.payment_effect != null
        ? describePaymentEffect(result.payment_effect)
        : "The final payment effect is not available in this response. Check current payment details before assuming a waiver, refund or credit change." },
      { value: `Payment record before cancellation: ${describePayment(result.payment)}. This historical status does not state the current liability; the committed effect above governs what is now owed.` },
      { value: `Final lesson state: ${readableState(result.booking_status)}.` },
      ...(result.replayed ? [{ value: "The earlier cancellation was confirmed; it was not repeated." }] : []),
    ] };
}
function movementOutcome(result: MutationResult, lesson: StudentLesson, destination: string): ActionOutcome {
  return { variant: "success", title: "Lesson moved",
    description: `${formatDate(lesson.startsAt)} · ${formatTime(lesson.startsAt)} → ${formatDate(destination)} · ${formatTime(destination)}`,
    details: [{ value: `Moved to ${formatDate(destination)} at ${formatTime(destination)}.` },
      { value: describeCredit(result.credit, "confirmed") }, { value: describePayment(result.payment) },
      ...(result.replayed ? [{ value: "The earlier move was confirmed; the lesson was not moved again." }] : []),
    ] };
}

export type ChangeMode = "move" | "cancel";

type CancellationAttempt = { key: string; reason: string; uncertainReason: string | null };

/**
 * Changing one lesson starts with a safe choice, not a destructive default.
 *
 * The browser still decides none of the policy. Availability, reschedule
 * permission, notice-period consequences, credits and payment outcomes all
 * come from the existing FastAPI contracts. This component only presents those
 * answers in a reschedule-first order and makes the final cancellation explicit.
 */
export function ChangeLesson({
  studentId,
  lesson,
  onDone,
  onClose,
  initialMode,
}: {
  studentId: string;
  lesson: StudentLesson;
  onDone: () => void;
  onClose: () => void;
  /** Let a caller enter the shared flow at the requested action. */
  initialMode?: ChangeMode;
}) {
  const [mode, setMode] = useState<ChangeMode | null>(initialMode ?? null);
  // Attempt identity belongs to the whole Change lesson session, not to a child
  // panel. Backing out to the chooser must not turn an uncertain retry into a
  // fresh cancellation or reschedule request.
  const cancellationScope = JSON.stringify([studentId, lesson.bookingId]);
  const [cancelAttempts, setCancelAttempts] = useState<Record<string, CancellationAttempt>>({});
  const cancelAttempt = useMemo(
    () => cancelAttempts[cancellationScope] ?? { key: idempotencyKey(), reason: "", uncertainReason: null },
    [cancelAttempts, cancellationScope],
  );
  const [moveKey, setMoveKey] = useState(idempotencyKey);

  useEffect(() => {
    setMode(initialMode ?? null);
  }, [initialMode, studentId, lesson.bookingId]);

  const policyActions = lesson.enrichment?.actions;
  const canMove = policyActions?.reschedule.allowed ?? true;
  const canCancel = policyActions?.cancel.allowed ?? true;

  return (
    <div className="panel change-lesson">
      <div className="spread">
        <strong>
          {formatDate(lesson.startsAt)} · {formatTime(lesson.startsAt)}
        </strong>
        <button className="quiet small" onClick={onClose} type="button">
          Close
        </button>
      </div>
      <p className="small muted" style={{ marginTop: 2 }}>
        {lesson.serviceName ?? "Lesson"}
        {lesson.tutorName && ` with ${lesson.tutorName}`}
      </p>

      {mode === null ? (
        <>
          <p className="small" style={{ marginBottom: 10 }}>
            What would you like to change?
          </p>
          <div className="row">
            {canMove && (
              <button className="action-primary" onClick={() => setMode("move")} type="button">
                Find another time
              </button>
            )}
            {canCancel && (
              <button className="action-danger" onClick={() => setMode("cancel")} type="button">
                Cancel this lesson
              </button>
            )}
          </div>
          {policyActions && (
            <>
              <Reasons decision={policyActions.reschedule} />
              <Reasons decision={policyActions.cancel} />
            </>
          )}
        </>
      ) : (
        <>
          <button
            className="quiet small"
            onClick={() => setMode(null)}
            style={{ margin: "4px 0 12px" }}
            type="button"
          >
            ← Back to change options
          </button>
          {mode === "cancel" ? (
            <CancelLesson
              key={cancellationScope}
              studentId={studentId}
              lesson={lesson}
              onDone={onDone}
              attemptKey={cancelAttempt.key}
              reason={cancelAttempt.reason}
              uncertainReason={cancelAttempt.uncertainReason}
              onReasonChange={(reason) =>
                setCancelAttempts((current) => {
                  const existing = current[cancellationScope] ?? cancelAttempt;
                  return { ...current, [cancellationScope]: { ...existing, reason } };
                })
              }
              onAttemptKeyChange={(key) =>
                setCancelAttempts((current) => {
                  const existing = current[cancellationScope] ?? cancelAttempt;
                  return { ...current, [cancellationScope]: { ...existing, key } };
                })
              }
              onUncertainAttempt={(reason) =>
                setCancelAttempts((current) => {
                  const existing = current[cancellationScope] ?? cancelAttempt;
                  return { ...current, [cancellationScope]: { ...existing, uncertainReason: reason } };
                })
              }
            />
          ) : (
            <MoveLesson
              studentId={studentId}
              lesson={lesson}
              onDone={onDone}
              attemptKey={moveKey}
              onAttemptKeyChange={setMoveKey}
            />
          )}
        </>
      )}
    </div>
  );
}

/** The message a failed read or a definite mutation refusal deserves. */
function failure(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  return "Something went wrong. Nothing was changed.";
}

function readableState(state: string): string {
  const labels: Record<string, string> = {
    pending: "Awaiting tutor confirmation",
    approved: "Confirmed",
    canceled: "Cancelled",
    cancelled: "Cancelled",
    rejected: "Declined",
    completed: "Completed",
    no_show: "Marked as a missed lesson",
    unknown: "Status unavailable",
  };
  return labels[state] ?? "Status unavailable";
}

const uncertainCancellationMessage =
  "The cancellation outcome is uncertain. Your lesson list is being refreshed; check whether this lesson is still listed before trying again. A retry here will reuse the same cancellation attempt.";

const uncertainRescheduleMessage =
  "The move outcome is uncertain. Your lesson list is being refreshed; check this lesson's time before trying again. A retry here will reuse the same move attempt.";

function cancellationPreviewDecision(preview: CancellationPreview): string {
  return JSON.stringify({
    booking_id: preview.booking_id,
    starts_at: preview.starts_at,
    notice_satisfied: preview.notice_satisfied,
    cancellation: preview.cancellation,
    credit: preview.credit,
    payment_effect: preview.payment_effect,
    resulting_outcome: preview.resulting_outcome,
  });
}

function sameInstant(left: string, right: string): boolean {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  return Number.isFinite(leftTime) && leftTime === rightTime;
}

function CancelLesson({
  studentId,
  lesson,
  onDone,
  attemptKey,
  reason,
  uncertainReason,
  onReasonChange,
  onAttemptKeyChange,
  onUncertainAttempt,
}: {
  studentId: string;
  lesson: StudentLesson;
  onDone: () => void;
  attemptKey: string;
  reason: string;
  uncertainReason: string | null;
  onReasonChange: (reason: string) => void;
  onAttemptKeyChange: (key: string) => void;
  onUncertainAttempt: (reason: string | null) => void;
}) {
  const [preview, setPreview] = useState<{ scope: string; value: CancellationPreview } | null>(null);
  const [previewError, setPreviewError] = useState<{ scope: string; message: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRevision, setPreviewRevision] = useState(0);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [checkingCurrentPreview, setCheckingCurrentPreview] = useState(false);
  const mutationBusyRef = useRef(false);
  const previewRequestRef = useRef<{
    key: string;
    promise: Promise<CancellationPreview>;
    attached: boolean;
  } | null>(null);
  const mountedRef = useRef(false);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const previewScope = JSON.stringify([
    studentId,
    lesson.bookingId,
    lesson.startsAt,
    lesson.status,
    lesson.enrichment?.evaluated_at ?? null,
  ]);
  const currentPreviewScopeRef = useRef(previewScope);
  currentPreviewScopeRef.current = previewScope;
  const previewRequestKey = JSON.stringify([previewScope, previewRevision]);
  const currentPreview = preview?.scope === previewScope ? preview.value : null;
  const currentPreviewError = previewError?.scope === previewScope ? previewError.message : null;
  const previewMatchesLesson = !currentPreview || sameInstant(currentPreview.starts_at, lesson.startsAt);
  const previousPreviewScope = useRef(previewScope);
  const publish = usePublishActionOutcome();
  const [hosted, setHosted] = useState(false);
  const [done, setDone] = useState<MutationResult | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const reasonLocked = uncertainReason !== null;

  useEffect(() => {
    if (previousPreviewScope.current === previewScope) return;
    previousPreviewScope.current = previewScope;
    setMutationError(null);
  }, [previewScope]);

  // Preview on entry and when the lesson or its policy changes. The optional
  // note belongs to the final cancellation request and does not affect policy.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let request = previewRequestRef.current;
    if (!request || request.key !== previewRequestKey) {
      request = {
        key: previewRequestKey,
        promise: booking.cancellationPreview(studentId, lesson.bookingId, ""),
        attached: false,
      };
      previewRequestRef.current = request;
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(true);
    }
    if (request.attached) return;
    request.attached = true;
    const currentRequest = request;
    void currentRequest.promise
      .then((value) => {
        if (mountedRef.current && previewRequestRef.current === currentRequest) {
          setPreview({ scope: previewScope, value });
        }
      })
      .catch((cause: unknown) => {
        if (mountedRef.current && previewRequestRef.current === currentRequest) {
          setPreviewError({ scope: previewScope, message: failure(cause) });
        }
      })
      .finally(() => {
        if (mountedRef.current && previewRequestRef.current === currentRequest) setPreviewLoading(false);
      });
  }, [studentId, lesson.bookingId, previewRevision, previewScope]);

  // On a phone the final button sits below the consequences, under the fixed
  // bottom navigation. Bring it into view once the consequences have loaded.
  const consequencesShown = currentPreview !== null;
  useEffect(() => {
    if (consequencesShown) cancelButtonRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [consequencesShown]);

  async function confirm() {
    if (
      mutationBusyRef.current ||
      !currentPreview?.cancellation.allowed ||
      !previewMatchesLesson ||
      previewLoading
    ) return;
    const confirmedPreview = currentPreview;
    const confirmedScope = previewScope;
    const submittedReason = reason.trim();
    mutationBusyRef.current = true;
    setMutationBusy(true);
    setCheckingCurrentPreview(true);
    setMutationError(null);

    let freshPreview: CancellationPreview;
    try {
      freshPreview = await booking.cancellationPreview(studentId, lesson.bookingId, "");
    } catch (cause) {
      if (mountedRef.current && currentPreviewScopeRef.current === confirmedScope) {
        setPreview(null);
        setPreviewError({ scope: confirmedScope, message: failure(cause) });
      }
      mutationBusyRef.current = false;
      setMutationBusy(false);
      setCheckingCurrentPreview(false);
      return;
    }

    if (!mountedRef.current || currentPreviewScopeRef.current !== confirmedScope) {
      mutationBusyRef.current = false;
      setMutationBusy(false);
      setCheckingCurrentPreview(false);
      return;
    }

    if (!sameInstant(freshPreview.starts_at, lesson.startsAt)) {
      setPreview({ scope: confirmedScope, value: freshPreview });
      mutationBusyRef.current = false;
      setMutationBusy(false);
      setCheckingCurrentPreview(false);
      onDone();
      return;
    }

    if (cancellationPreviewDecision(freshPreview) !== cancellationPreviewDecision(confirmedPreview)) {
      setPreview({ scope: confirmedScope, value: freshPreview });
      setMutationError(freshPreview.cancellation.allowed
        ? "The cancellation consequences changed. Review the updated details before continuing."
        : "Cancellation is no longer available under the current policy. Review the updated details.");
      mutationBusyRef.current = false;
      setMutationBusy(false);
      setCheckingCurrentPreview(false);
      return;
    }

    setCheckingCurrentPreview(false);
    try {
      const result = await booking.cancel(studentId, lesson.bookingId, submittedReason, attemptKey);
      setDone(result);
      setHosted(publish(cancellationOutcome(result, lesson)));
      onDone();
    } catch (cause) {
      const kind = bookingFailureKind(cause);
      setMutationError(kind === "uncertain" ? uncertainCancellationMessage : failure(cause));
      setUncertain(kind === "uncertain");
      if (kind === "rejected") {
        onAttemptKeyChange(idempotencyKey());
        onUncertainAttempt(null);
      }
      if (kind === "uncertain") {
        onUncertainAttempt(reason);
        setHosted(publish({ variant: "uncertain", title: "Cancellation outcome needs checking", description: `${formatDate(lesson.startsAt)} · ${formatTime(lesson.startsAt)}`, details: [{ value: uncertainCancellationMessage }] }));
        onDone();
      }
      setPreview(null);
      // Even a failed mutation can coincide with changed server policy. Obtain
      // a fresh authoritative preview before offering a retry. An uncertain
      // retry keeps both its original reason and idempotency key.
      setPreviewRevision((current) => current + 1);
    } finally {
      mutationBusyRef.current = false;
      setMutationBusy(false);
      setCheckingCurrentPreview(false);
    }
  }

  if (done && hosted) return null;
  if (done) return <ActionConfirmation {...cancellationOutcome(done, lesson)} reveal />;

  return (
    <>
      <label className="stack">
        <span className="small muted">Why, so the tutor knows (optional)</span>
        <input
          value={reason}
          disabled={mutationBusy || reasonLocked}
          onChange={(event) => {
            const nextReason = event.target.value;
            onReasonChange(nextReason);
            setMutationError(null);
            setUncertain(false);
          }}
          maxLength={200}
        />
      </label>

      {previewLoading && !currentPreview && (
        <p className="small muted cancellation-preview-status" role="status">
          Checking cancellation consequences…
        </p>
      )}

      {currentPreviewError && (
        <div className="error cancellation-preview-error" role="alert">
          <div>{currentPreviewError}</div>
          <button
            className="quiet small cancellation-preview-retry"
            disabled={previewLoading || mutationBusy}
            onClick={() => {
              setPreviewError(null);
              setPreviewRevision((current) => current + 1);
            }}
            type="button"
          >
            Retry cancellation check
          </button>
        </div>
      )}

      {currentPreview && !previewMatchesLesson && (
        <div className="error cancellation-preview-error" role="alert">
          <div>The cancellation check refers to a different lesson time. Refresh the lesson list before cancelling.</div>
          <button
            className="quiet small"
            disabled={previewLoading || mutationBusy}
            onClick={onDone}
            type="button"
          >
            Refresh lesson details
          </button>
        </div>
      )}

      {mutationError && (
        <>{uncertain ? !hosted && <ActionConfirmation variant="uncertain" title="Cancellation outcome needs checking" description={mutationError} reveal /> : <div className="error cancellation-preview-error" role="alert">{mutationError}</div>}</>
      )}

      {currentPreview && (
        <div className="notice" style={{ marginTop: 12 }}>
          <p style={{ marginTop: 0 }}>
            <strong>Cancellation consequences</strong>
          </p>
          <p className="small">
            <strong>Lesson time:</strong> {formatDate(currentPreview.starts_at)} · {formatTime(currentPreview.starts_at)}.
          </p>
          {!currentPreview.notice_satisfied && (
            <div className="notice late-cancellation-warning" role="alert">
              <strong>Late cancellation</strong>
              <p style={{ marginBottom: 0 }}>{lateCancellationMessage(currentPreview)}</p>
            </div>
          )}
          <p className="small">
            <strong>Notice period:</strong>{" "}
            {currentPreview.notice_satisfied ? "satisfied" : "inside the notice period"}.
          </p>
          <p className="small">
            <strong>Lesson credits:</strong> {describeCredit(currentPreview.credit)}
          </p>
          <p className="small">
            <strong>Payment:</strong> {describePaymentEffect(currentPreview.payment_effect)}
          </p>
          <p className="small">
            <strong>Notification status:</strong> not provided by the current cancellation preview.
          </p>
          <p className="small">
            <strong>Final lesson state:</strong> {readableState(currentPreview.resulting_outcome)}.
          </p>
          <Reasons decision={currentPreview.cancellation} />
        </div>
      )}

      <div className="row" style={{ marginTop: 10 }}>
        <button
          ref={cancelButtonRef}
          className="action-danger cancellation-final-action"
          disabled={mutationBusy || previewLoading || !currentPreview?.cancellation.allowed || !previewMatchesLesson}
          onClick={() => void confirm()}
          type="button"
        >
          {mutationBusy ? checkingCurrentPreview ? "Checking latest consequences…" : "Cancelling…" : "Cancel this lesson"}
        </button>
      </div>
    </>
  );
}

function MoveLesson({
  studentId,
  lesson,
  onDone,
  attemptKey,
  onAttemptKeyChange,
}: {
  studentId: string;
  lesson: StudentLesson;
  onDone: () => void;
  attemptKey: string;
  onAttemptKeyChange: (key: string) => void;
}) {
  const enrichment = lesson.enrichment;
  // The tutor and lesson stay locked to the existing booking. Availability is
  // still the family availability endpoint; no browser-side availability logic
  // is introduced here.
  const providerId = lesson.providerId ? String(lesson.providerId) : null;
  const serviceId = lesson.serviceId ? String(lesson.serviceId) : null;

  const [slots, setSlots] = useState<AvailabilitySlot[] | null>(null);
  const [range, setRange] = useState<AvailabilityRange>("week");
  const [rangeStart, setRangeStart] = useState(() => dayKey(lesson.startsAt) < isoDay(0) ? isoDay(0) : dayKey(lesson.startsAt));
  const lessonBrowseKey = useRef(`${lesson.bookingId}:${lesson.startsAt}`);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityRevision, setAvailabilityRevision] = useState(0);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReschedulePreview | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const publish = usePublishActionOutcome();
  const [hosted, setHosted] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [moveResult, setMoveResult] = useState<MutationResult | null>(null);
  const [uncertain, setUncertain] = useState(false);

  const window = availabilityWindow(rangeStart, range);
  const today = isoDay(0);

  useEffect(() => {
    const nextKey = `${lesson.bookingId}:${lesson.startsAt}`;
    if (lessonBrowseKey.current === nextKey) return;
    lessonBrowseKey.current = nextKey;
    setRangeStart(dayKey(lesson.startsAt) < isoDay(0) ? isoDay(0) : dayKey(lesson.startsAt));
    setRange("week");
    setChosen(null);
    setPreview(null);
    setActionError(null);
    setAvailabilityRevision((revision) => revision + 1);
  }, [lesson.bookingId, lesson.startsAt]);

  useEffect(() => {
    if (!providerId || !serviceId) {
      setAvailabilityLoading(false);
      return;
    }

    let cancelled = false;
    setAvailabilityLoading(true);
    setSlots(null);
    setAvailabilityError(null);

    void booking
      .availability(studentId, {
        provider_id: providerId,
        service_id: serviceId,
        start_date: window.startDate,
        end_date: window.endDate,
      })
      .then((availability) => {
        if (!cancelled) setSlots(availability.slots);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setAvailabilityError(failure(cause));
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    studentId,
    providerId,
    serviceId,
    window.startDate,
    window.endDate,
    availabilityRevision,
  ]);

  function clearChoice() {
    setChosen(null);
    setPreview(null);
    setActionError(null);
  }

  function changeRange(nextRange: AvailabilityRange) {
    if (nextRange === range) return;
    setRange(nextRange);
    clearChoice();
  }

  function moveWindow(direction: -1 | 1) {
    setRangeStart((current) => {
      const next = moveAvailabilityWindow(current, range, direction);
      // Day navigation can leave a week view off the original seven-day grid.
      // A Previous-week press from there must clamp at today, never ask the
      // authoritative endpoint for a range beginning in the past.
      return direction === -1 && next < today ? today : next;
    });
    clearChoice();
  }

  async function review(startsAt: string) {
    if (busy) return;
    setChosen(startsAt);
    setBusy(true);
    setActionError(null);
    setUncertain(false);
    setPreview(null);
    try {
      setPreview(await booking.reschedulePreview(studentId, lesson.bookingId, startsAt));
    } catch (cause) {
      setActionError(failure(cause));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!chosen || busy || !moveAllowed(preview)) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await booking.reschedule(studentId, lesson.bookingId, chosen, attemptKey);
      // A confirmed move completes this operation. A later different move in
      // the same panel needs a fresh identity; uncertain retries retain theirs.
      onAttemptKeyChange(idempotencyKey());
      setMoveResult(result);
      setDone(chosen);
      setHosted(publish(movementOutcome(result, lesson, chosen)));
      onDone();
    } catch (cause) {
      const kind = bookingFailureKind(cause);
      setActionError(kind === "uncertain" ? uncertainRescheduleMessage : failure(cause));
      setUncertain(kind === "uncertain");
      if (kind === "rejected") onAttemptKeyChange(idempotencyKey());
      if (kind === "uncertain") {
        setHosted(publish({ variant: "uncertain", title: "Move outcome needs checking", description: `${formatDate(lesson.startsAt)} · ${formatTime(lesson.startsAt)}`, details: [{ value: uncertainRescheduleMessage }] }));
        onDone();
      }
      setPreview(null);
      setChosen(null);
      // A refused or uncertain move makes the visible slot list stale. Refresh
      // from the authoritative availability endpoint before another choice. The
      // action error is separate so this refetch cannot erase an uncertainty warning.
      setAvailabilityRevision((current) => current + 1);
    } finally {
      setBusy(false);
    }
  }

  if (done && hosted) return null;
  if (done && moveResult) return <ActionConfirmation {...movementOutcome(moveResult, lesson, done)} reveal />;

  if (!providerId || !serviceId) {
    return (
      <p className="small muted">
        This lesson cannot be moved from here at the moment. Ask your tutor and they can move it for
        you.
      </p>
    );
  }

  const recurring = enrichment?.recurrence.student_change_scope === "single_occurrence";

  return (
    <>
      {recurring && (
        <div className="notice small">
          This is one of a repeating set of lessons. Moving it changes <strong>this lesson only</strong>;
          the rest stay where they are.
        </div>
      )}

      {actionError && (
        <>{uncertain ? !hosted && <ActionConfirmation variant="uncertain" title="Move outcome needs checking" description={actionError} reveal /> : <div className="error" role="alert">{actionError}</div>}</>
      )}

      {availabilityError && (
        <div className="error" role="alert">
          <div>{availabilityError}</div>
          <button
            disabled={busy || availabilityLoading}
            onClick={() => setAvailabilityRevision((current) => current + 1)}
            className="quiet small change-lesson-availability-retry"
            type="button"
          >
            Try again
          </button>
        </div>
      )}

      <div className="availability-controls">
        <div className="row" role="group" aria-label="Availability range">
          <button
            aria-pressed={range === "day"}
            className={range === "day" ? "" : "quiet"}
            disabled={busy || availabilityLoading}
            onClick={() => changeRange("day")}
            type="button"
          >
            Day
          </button>
          <button
            aria-pressed={range === "week"}
            className={range === "week" ? "" : "quiet"}
            disabled={busy || availabilityLoading}
            onClick={() => changeRange("week")}
            type="button"
          >
            Week
          </button>
        </div>
        <div className="row">
          <button
            aria-label={`Previous ${range}`}
            className="quiet"
            disabled={busy || availabilityLoading || rangeStart <= today}
            onClick={() => moveWindow(-1)}
            type="button"
          >
            ← Previous
          </button>
          <strong className="availability-window">
            {range === "day"
              ? formatFullDay(window.startDate)
              : `${formatFullDay(window.startDate)} – ${formatFullDay(
                  moveAvailabilityWindow(window.endDate, "day", -1),
                )}`}
          </strong>
          <button
            aria-label={`Next ${range}`}
            className="quiet"
            disabled={busy || availabilityLoading}
            onClick={() => moveWindow(1)}
            type="button"
          >
            Next →
          </button>
        </div>
      </div>

      {availabilityLoading && (
        <p className="small muted" role="status">
          Looking for free times…
        </p>
      )}

      {slots !== null && slots.length === 0 && (
        <p className="empty">
          No free times in this {range}. Try another {range === "day" ? "day" : "week"}.
        </p>
      )}

      {slots !== null && slots.length > 0 && (
        <div className="availability-days">
          {groupAvailabilitySlots(slots).map(([date, daySlots]) => (
            <section className="availability-day" key={date}>
              <h3 className="day-heading">{formatDay(daySlots[0]!.starts_at)}</h3>
              <div className="slot-grid">
                {daySlots.map((slot) => {
                  const selected = chosen === slot.starts_at;
                  return (
                    <button
                      aria-pressed={selected}
                      key={slot.starts_at}
                      className={selected ? "" : "quiet"}
                      disabled={!selectableForMove(slot) || busy}
                      onClick={() => void review(slot.starts_at)}
                      type="button"
                    >
                      {formatTime(slot.starts_at)}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <MoveTimeRequestEntry studentId={studentId} lesson={lesson} onChanged={onDone} onFree={slot => { setSlots(current => [...(current ?? []).filter(row => row.starts_at !== slot.starts_at), slot]); void review(slot.starts_at); }} />

      {preview && chosen && (
        <div className="notice" style={{ marginTop: 12 }}>
          <p style={{ marginTop: 0 }}>
            <strong>Review this move</strong>
          </p>
          <p className="small">
            Move to {formatDate(chosen)} at {formatTime(chosen)}.
          </p>
          <p className="small">{describeCredit(preview.credit)}</p>
          <p className="small">{describePaymentEffect(preview.payment_effect)}</p>
          <Reasons decision={preview.reschedule} />
          <div className="row" style={{ marginTop: 10 }}>
            <button disabled={busy || !moveAllowed(preview)} onClick={() => void confirm()} type="button">
              {busy ? "Moving…" : "Move the lesson"}
            </button>
            <button className="quiet" disabled={busy} onClick={clearChoice} type="button">
              Choose another time
            </button>
          </div>
        </div>
      )}
    </>
  );
}
